import {
  type ButtonHTMLAttributes,
  forwardRef,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from './format';
import { Icon, type IconName } from './icons';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'outlineDanger';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-primary text-primary-foreground shadow-sm hover:brightness-110 focus-visible:outline-primary',
  secondary: 'border-border bg-card text-foreground shadow-sm hover:bg-surface focus-visible:outline-foreground/40',
  danger: 'border-transparent bg-danger text-white shadow-sm hover:brightness-110 focus-visible:outline-danger dark:text-black',
  success: 'border-success/50 bg-success/15 text-success hover:bg-success/25 focus-visible:outline-success',
  outlineDanger: 'border-danger/50 bg-danger/10 text-danger hover:bg-danger/20 focus-visible:outline-danger',
  ghost: 'border-transparent bg-transparent text-foreground hover:bg-surface focus-visible:outline-foreground/40',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'icon';
  loading?: boolean;
  icon?: IconName;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[calc(var(--radius)*0.75)] border font-medium whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' && 'min-h-9 px-3 text-sm',
        size === 'md' && 'min-h-11 px-4 text-sm md:min-h-10',
        size === 'icon' && 'size-10 shrink-0 p-0',
        block && 'w-full',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon ? <Icon name={icon} size={16} /> : null}
      {children}
    </button>
  );
});

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg className={cn('animate-spin', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" role="status" aria-label="Yükleniyor">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Card({ className, children, padded = true }: { className?: string; children: ReactNode; padded?: boolean }) {
  return <div className={cn('min-w-0 rounded-[var(--radius)] border border-border bg-card', padded && 'p-4', className)}>{children}</div>;
}

/** Web Project'teki PageSection: başlıklı kart. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('min-w-0 rounded-[var(--radius)] border border-border bg-card', className)}>
      {title || actions ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {title ? <h2 className="text-base font-semibold">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn('p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between md:mb-6">
      <div className="min-w-0">
        {back ? <div className="mb-1">{back}</div> : null}
        <h1 className="truncate text-xl font-semibold tracking-tight md:text-2xl">{title}</h1>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'border-border bg-surface text-muted',
  primary: 'border-primary/30 bg-primary/10 text-primary',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  accent: 'border-accent/30 bg-accent/10 text-accent',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap', TONES[tone], className)}>{children}</span>;
}

export function Alert({ tone = 'primary', title, children, icon, action }: { tone?: Tone; title?: ReactNode; children?: ReactNode; icon?: IconName; action?: ReactNode }) {
  return (
    <div className={cn('flex gap-3 rounded-[var(--radius)] border p-3 text-sm', TONES[tone])} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon ?? (tone === 'danger' || tone === 'warning' ? 'alert' : 'info')} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 text-foreground">
        {title ? <div className="font-medium">{title}</div> : null}
        {children ? <div className={cn(title ? 'mt-0.5 text-muted' : '')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('ui-skeleton h-4', className)} />;
}

export function EmptyState({ icon = 'list', title, description, action }: { icon?: IconName; title: ReactNode; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-surface text-muted">
        <Icon name={icon} size={22} />
      </div>
      <div className="font-medium">{title}</div>
      {description ? <div className="max-w-sm text-sm text-muted">{description}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, icon, tone, className }: { label: ReactNode; value: ReactNode; hint?: ReactNode; icon?: IconName; tone?: Tone; className?: string }) {
  return (
    <Card className={className}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-medium tracking-wide text-muted uppercase">{label}</div>
          <div className="mt-1 truncate text-xl font-semibold tabular-nums md:text-2xl">{value}</div>
          {hint ? <div className="mt-1 text-xs text-muted">{hint}</div> : null}
        </div>
        {icon ? (
          <div className={cn('flex size-9 shrink-0 items-center justify-center rounded-full border', TONES[tone ?? 'primary'])}>
            <Icon name={icon} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function KeyValue({ items, className }: { items: { label: ReactNode; value: ReactNode }[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2', className)}>
      {items.map((it, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-xs text-muted">{it.label}</dt>
          <dd className="mt-0.5 text-sm break-words">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ----------------------------------- Form ----------------------------------- */

export function Field({ label, hint, error, children, required, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; required?: boolean; className?: string; htmlFor?: string }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
          {required ? <span className="ml-0.5 text-danger">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <p className="text-xs text-danger">{error}</p> : hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

const controlClass =
  'w-full min-w-0 rounded-[calc(var(--radius)*0.75)] border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted/70 transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leftIcon?: IconName;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, hint, error, leftIcon, className, id, required, type, ...rest }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  const input = (
    <div className="relative">
      {leftIcon ? <Icon name={leftIcon} size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" /> : null}
      <input
        ref={ref}
        id={inputId}
        required={required}
        type={isPassword && show ? 'text' : type}
        aria-invalid={error ? true : undefined}
        className={cn(controlClass, 'min-h-11 md:min-h-10', leftIcon && 'pl-9', isPassword && 'pr-10', error && 'border-danger', className)}
        {...rest}
      />
      {isPassword ? (
        <button
          type="button"
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-muted hover:text-foreground"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Şifreyi gizle' : 'Şifreyi göster'}
        >
          <Icon name={show ? 'eyeOff' : 'eye'} size={16} />
        </button>
      ) : null}
    </div>
  );
  if (!label && !hint && !error) return input;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      {input}
    </Field>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ label, hint, error, className, id, required, ...rest }, ref) {
  const autoId = useId();
  const el = <textarea ref={ref} id={id ?? autoId} required={required} className={cn(controlClass, 'min-h-24 py-2', error && 'border-danger', className)} {...rest} />;
  if (!label && !hint && !error) return el;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id ?? autoId}>
      {el}
    </Field>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Native select ile aynı biçimde `e.target.value` okunabilsin diye. */
export interface SelectChange {
  target: { value: string; name?: string };
}

export interface SelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: SelectOption[];
  placeholder?: string;
  value?: string | number;
  defaultValue?: string;
  onChange?: (e: SelectChange) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  title?: string;
  'aria-label'?: string;
}

const LIST_MAX_HEIGHT = 288;
const LIST_GAP = 4;

interface ListPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

function measure(trigger: HTMLElement): ListPosition {
  const r = trigger.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - LIST_GAP - 8;
  const above = r.top - LIST_GAP - 8;
  const up = below < Math.min(LIST_MAX_HEIGHT, 200) && above > below;
  return {
    left: r.left,
    width: r.width,
    maxHeight: Math.max(120, Math.min(LIST_MAX_HEIGHT, up ? above : below)),
    ...(up ? { bottom: window.innerHeight - r.top + LIST_GAP } : { top: r.bottom + LIST_GAP }),
  };
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, value, defaultValue, onChange, disabled, required, id, name, className, autoFocus, title, 'aria-label': ariaLabel },
  ref,
) {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const listId = `${triggerId}-list`;
  const items = useMemo<SelectOption[]>(() => (placeholder !== undefined ? [{ value: '', label: placeholder }, ...options] : options), [options, placeholder]);

  const [inner, setInner] = useState(defaultValue ?? '');
  const current = value !== undefined ? String(value) : inner;
  const selected = items.find((o) => o.value === current);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<ListPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const setRefs = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const step = useCallback(
    (from: number, dir: 1 | -1) => {
      for (let i = 1; i <= items.length; i++) {
        const idx = (from + dir * i + items.length * 2) % items.length;
        if (!items[idx]?.disabled) return idx;
      }
      return from;
    },
    [items],
  );

  const show = () => {
    if (disabled || !items.length) return;
    const idx = items.findIndex((o) => o.value === current && !o.disabled);
    setActive(idx >= 0 ? idx : step(-1, 1));
    if (triggerRef.current) setPos(measure(triggerRef.current));
    setOpen(true);
  };

  const hide = () => setOpen(false);

  const choose = (idx: number) => {
    const opt = items[idx];
    if (!opt || opt.disabled) return;
    hide();
    triggerRef.current?.focus();
    if (opt.value === current) return;
    if (value === undefined) setInner(opt.value);
    onChange?.({ target: { value: opt.value, name } });
  };

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => triggerRef.current && setPos(measure(triggerRef.current));
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const typeAhead = (ch: string) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at > 700 ? ch : t.text + ch;
    t.at = now;
    const q = t.text.toLocaleLowerCase('tr');
    const start = open ? active : items.findIndex((o) => o.value === current);
    for (let i = 1; i <= items.length; i++) {
      const idx = (start + i + items.length) % items.length;
      const o = items[idx];
      if (o && !o.disabled && o.label.toLocaleLowerCase('tr').startsWith(q)) {
        if (open) setActive(idx);
        else choose(idx);
        return;
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (!open) show();
        else setActive((a) => step(a, e.key === 'ArrowDown' ? 1 : -1));
        return;
      case 'Home':
      case 'End':
        if (!open) return;
        e.preventDefault();
        setActive(e.key === 'Home' ? step(-1, 1) : step(items.length, -1));
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (open) choose(active);
        else show();
        return;
      case 'Escape':
        if (!open) return;
        e.preventDefault();
        e.stopPropagation();
        hide();
        return;
      case 'Tab':
        if (open) hide();
        return;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) typeAhead(e.key);
    }
  };

  const el = (
    <div className="relative">
      <button
        ref={setRefs}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        title={title}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        autoFocus={autoFocus}
        onClick={() => (open ? hide() : show())}
        onKeyDown={onKeyDown}
        className={cn(
          controlClass,
          'flex min-h-11 cursor-pointer items-center pr-9 text-left md:min-h-10',
          open && 'border-primary ring-2 ring-primary/25',
          error && 'border-danger',
          className,
        )}
      >
        <span className={cn('truncate', (!selected || selected.value === '') && placeholder !== undefined && 'text-muted')}>{selected?.label ?? placeholder ?? ''}</span>
      </button>
      <Icon
        name="chevronDown"
        size={16}
        className={cn('pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted transition-transform', open && 'rotate-180')}
      />
      {open && pos
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-labelledby={triggerId}
              style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
              className="ui-anim-fade-in fixed z-[70] overflow-y-auto overscroll-contain rounded-[calc(var(--radius)*0.75)] border border-border bg-card p-1 text-sm text-foreground shadow-lg"
            >
              {items.map((o, i) => {
                const isSelected = o.value === current;
                return (
                  <li
                    key={o.value}
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={o.disabled || undefined}
                    onPointerMove={() => !o.disabled && active !== i && setActive(i)}
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => choose(i)}
                    className={cn(
                      'flex min-h-10 cursor-pointer items-center gap-2 rounded-[calc(var(--radius)*0.5)] px-2.5 py-2 md:min-h-9',
                      i === active && 'bg-surface',
                      isSelected && 'font-medium text-primary',
                      o.value === '' && placeholder !== undefined && !isSelected && 'text-muted',
                      o.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {isSelected ? <Icon name="check" size={16} className="shrink-0" /> : null}
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
  if (!label && !hint && !error) return el;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={triggerId}>
      {el}
    </Field>
  );
});

export function Checkbox({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <label htmlFor={id} className={cn('flex cursor-pointer items-start gap-3 py-1', disabled && 'cursor-not-allowed opacity-60')}>
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-border accent-[var(--primary)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {description ? <span className="block text-xs text-muted">{description}</span> : null}
      </span>
    </label>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const btn = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition disabled:opacity-50',
        checked ? 'border-primary bg-primary' : 'border-border bg-surface',
      )}
    >
      <span className={cn('inline-block size-5 rounded-full bg-white shadow transition', checked ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  );
  if (!label) return btn;
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        {description ? <div className="text-xs text-muted">{description}</div> : null}
      </div>
      {btn}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Ara…', className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cn('relative w-full sm:w-64', className)}>
      <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cn(controlClass, 'min-h-11 pl-9 md:min-h-10')} />
    </div>
  );
}

/** Seçenekler arasından tek seçim (segment). Mobilde yatay kayar. */
export function Segmented<T extends string>({ value, onChange, options, className }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; className?: string }) {
  return (
    <div className={cn('flex max-w-full gap-1 overflow-x-auto rounded-[calc(var(--radius)*0.75)] border border-border bg-surface p-1', className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={cn(
            'min-h-9 shrink-0 rounded-[calc(var(--radius)*0.5)] px-3 text-sm font-medium whitespace-nowrap transition',
            o.value === value ? 'bg-card text-foreground shadow-sm' : 'text-muted hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Sekmeli içerik başlığı (alt çizgili). */
export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode; hidden?: boolean }[] }) {
  return (
    <div className="-mx-4 mb-4 overflow-x-auto border-b border-border px-4 md:mx-0 md:px-0" role="tablist">
      <div className="flex gap-1">
        {tabs
          .filter((t) => !t.hidden)
          .map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={t.value === value}
              onClick={() => onChange(t.value)}
              className={cn(
                '-mb-px min-h-11 shrink-0 border-b-2 px-3 text-sm font-medium whitespace-nowrap transition',
                t.value === value ? 'border-primary text-foreground' : 'border-transparent text-muted hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
      </div>
    </div>
  );
}

/** 6 haneli doğrulama kodu (TOTP). Yapıştırma ve SMS otomatik doldurma desteklenir. */
export function OtpInput({ value, onChange, length = 6, autoFocus, disabled }: { value: string; onChange: (v: string) => void; length?: number; autoFocus?: boolean; disabled?: boolean }) {
  return (
    <input
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={length}
      autoFocus={autoFocus}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, length))}
      className={cn(controlClass, 'min-h-12 text-center font-mono text-2xl tracking-[0.5em]')}
      placeholder={'•'.repeat(length)}
      aria-label="Doğrulama kodu"
    />
  );
}
